// RichTextContent — tárolt rich-text HTML BIZTONSÁGOS megjelenítése. A `dangerouslySetInnerHTML` KIZÁRÓLAG itt
// szerepel az egész appban, és CSAK a `sanitizeRichTextHtml` (DOMPurify + szűk allowlist) UTÁN. Ez a stored-XSS
// elleni fő védelmi pont: a migrált legacy WYSIWYG-HTML és az élő szerkesztő kimenete is ezen az úton renderel.
// A tipográfiát a Tailwind `prose` adja (a design-token szövegszínekre hangolva).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaLightbox, type MediaLightboxItem } from '@/components/shared/media-lightbox';
import { hydrateEditorImages, type EditorImageResolverConfig } from '@/lib/editor-image-src';
import { sanitizeRichTextHtml } from '@/lib/sanitize-html';
import { cn } from '@/lib/utils';

export interface RichTextContentProps {
  /** A tárolt HTML (nem megbízható — sanitizálás előtt semmi sem kerül a DOM-ba). */
  html: string;
  /**
   * Elhagyható, FELÜLET-specifikus kép-feloldó. A beágyazott képek a tárolt, stabil utat viselik; a
   * megjelenítés előtt egy hitelesített hívás oldja fel presigned URL-re. Alap: a BELSŐ végpont. A
   * PARTNER-felület a saját, szűk végpontját adja át (a belső kép-út nem nyílhat meg a partnernek).
   */
  imageResolver?: EditorImageResolverConfig;
  /**
   * `false` → a beágyazott képek/videók NEM kattinthatók (nincs nagyító). Alap: BE.
   *
   * MIÉRT ALAPBÓL BE: a beágyazott kép jellemzően KÉPERNYŐFELVÉTEL, amit a szöveg-szélességre zsugorítva
   * nem lehet elolvasni — épp azért került oda, hogy a részletet mutassa. A kattintásra nagyítás az iparági
   * alap (GitHub, Linear, Slack), és minden felületünkön ugyanazt akarjuk.
   */
  lightbox?: boolean;
  className?: string;
}

export function RichTextContent({
  html,
  className,
  imageResolver,
  lightbox = true,
}: RichTextContentProps): JSX.Element {
  const { t } = useTranslation();
  // A sanitizálás minden render-úton lefut; a memo csak a fölösleges újra-futást kerüli el (input-azonosságra).
  const clean = useMemo(() => sanitizeRichTextHtml(html), [html]);

  // A beágyazott képek `src`-je a TÁROLT, stabil út — a böngésző azt nem tudja betölteni (a BFF Bearer-tokent
  // kér). Ezért a DOM-ba kerülés UTÁN oldjuk fel friss presigned URL-re. Az abort a unmount-ra vonatkozik.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const controller = new AbortController();
    hydrateEditorImages(el, controller.signal, imageResolver);
    return () => controller.abort();
  }, [clean, imageResolver]);

  const [items, setItems] = useState<MediaLightboxItem[]>([]);
  const [index, setIndex] = useState<number | null>(null);

  /**
   * A MÉDIA-LISTA a KATTINTÁS PILLANATÁBAN, a DOM-ból gyűlik — nem a HTML-ből előre.
   *
   * MIÉRT ÍGY: a `src` ekkor már a HIDRATÁLT, friss presigned URL (a tárolt stabil útból a nagyító nem
   * tudna képet mutatni). Egy előre kiszámolt lista vagy a stabil utakat tartaná (használhatatlan), vagy
   * meg kellene várnia az összes feloldást — a kattintáskori gyűjtés viszont pontosan azt látja, ami a
   * felhasználó előtt van. Mellékhaszon: nincs állapot, amit szinkronban kell tartani a tartalommal.
   */
  const openAt = useCallback(
    (target: HTMLElement) => {
      const el = ref.current;
      if (el === null) return;
      const media = [...el.querySelectorAll<HTMLElement>('img, video')].filter((m) => {
        const src = m.getAttribute('src');
        // A MÉG FEL NEM OLDOTT (src nélküli) elem kimarad: a nagyítóban üres lap lenne.
        return src !== null && src !== '';
      });
      const at = media.indexOf(target);
      if (at < 0) return;
      setItems(
        media.map((m) => ({
          src: m.getAttribute('src') ?? '',
          // KÉP: `alt`; VIDEÓ: `title` (az `alt` nem érvényes attribútum a `<video>`-n).
          alt: m.getAttribute('alt') ?? m.getAttribute('title') ?? t('lightbox.untitled'),
          kind: m.tagName === 'VIDEO' ? ('video' as const) : ('image' as const),
        })),
      );
      setIndex(at);
    },
    [t],
  );

  return (
    <>
      <div
        ref={ref}
        // ESEMÉNY-DELEGÁLÁS a konténeren: a képekre NEM teszünk külön kezelőt. A tartalom
        // `dangerouslySetInnerHTML`-lel kerül a DOM-ba, tehát React-eseményt nem lehet rá kötni, a kézzel
        // felkötött listener pedig minden hidratálás után újra-kötést kérne (és leszedést unmountnál).
        onClick={
          lightbox
            ? (e) => {
                const target = e.target as HTMLElement;
                // VIDEÓ: a KERETRE kattintás nagyít, de a lejátszó-vezérlők NEM — azok a böngészőé (a
                // `controls` shadow-DOM-ja nem is jön vissza `e.target`-ként, ezért a videó törzsére
                // kattintás nagyít, a play/szünet gomb nem).
                if (target.tagName === 'IMG' || target.tagName === 'VIDEO') openAt(target);
              }
            : undefined
        }
        className={cn(
          // `prose` tipográfia a design szövegszíneivel; a linkek a brand-tónust kapják.
          'prose prose-sm dark:prose-invert max-w-none',
          'prose-a:text-brand-hover prose-a:no-underline hover:prose-a:underline',
          'text-foreground text-sm',
          // A NAGYÍTHATÓSÁG LÁTSZIK is: nagyító-kurzor a képeken (a videó a saját lejátszó-kurzorát tartja).
          lightbox && 'prose-img:cursor-zoom-in',
          className,
        )}
        // A `dangerouslySetInnerHTML` CSAK a fenti DOMPurify-sanitizálás után fut. (Az appban EGY másik
        // beszúrás-hely van: `components/messages/message-detail-sheet.tsx` — ott a BFF `sanitizeMessageBody`-ja
        // a fal, ÍRÁSKOR és OLVASÁSKOR is. Új rich-text megjelenítéshez EZT a komponenst használd, hogy a
        // kliens-oldali sanitálás is meglegyen — a kettős védelem a migrált legacy HTML miatt sem elméleti.)
        dangerouslySetInnerHTML={{ __html: clean }}
      />
      {lightbox && (
        <MediaLightbox
          items={items}
          index={index}
          onClose={() => setIndex(null)}
          onIndexChange={setIndex}
        />
      )}
    </>
  );
}
